import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", category: "Agilité", author: "ASAP Technologies X Reqen", read_time: "5 min", image_url: "", published: true };

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (editing.article_id) await api.put(`/admin/articles/${editing.article_id}`, editing);
      else await api.post("/admin/articles", editing);
      toast.success("Article enregistré");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/articles/${id}`);
    toast.success("Article supprimé");
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Articles ({articles.length})</h2>
        <button data-testid="article-create-btn" onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2 !px-4 text-xs">
          <Plus size={14} /> Nouvel article
        </button>
      </div>
      <div className="space-y-3">
        {articles.map((a) => (
          <div key={a.article_id} className="border border-white/10 bg-[#111827] p-5 flex items-center gap-4" data-testid={`article-row-${a.article_id}`}>
            <div className="flex-1">
              <p className="text-sm text-slate-200 font-medium">{a.title}</p>
              <p className="text-xs text-slate-500 mt-1">
                {a.category} · {a.read_time} · /{a.slug} {!a.published && <span className="text-amber-400">· Brouillon</span>}
              </p>
            </div>
            <button data-testid={`article-edit-${a.article_id}`} onClick={() => setEditing(a)} className="text-slate-500 hover:text-teal-300 transition-colors"><Pencil size={16} /></button>
            <button data-testid={`article-delete-${a.article_id}`} onClick={() => remove(a.article_id)} className="text-slate-600 hover:text-rose-400 transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0f1524] border-white/10 text-slate-100 rounded-none" data-testid="article-editor-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{editing.article_id ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <input data-testid="article-input-title" className="input-dark" placeholder="Titre" value={editing.title} onChange={set("title")} />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="article-input-category" className="input-dark" placeholder="Catégorie" value={editing.category} onChange={set("category")} />
                  <input data-testid="article-input-readtime" className="input-dark" placeholder="Temps de lecture" value={editing.read_time} onChange={set("read_time")} />
                </div>
                <input data-testid="article-input-image" className="input-dark" placeholder="URL de l'image (optionnel)" value={editing.image_url} onChange={set("image_url")} />
                <textarea data-testid="article-input-excerpt" className="input-dark min-h-[70px]" placeholder="Accroche / résumé" value={editing.excerpt} onChange={set("excerpt")} />
                <textarea data-testid="article-input-content" className="input-dark min-h-[220px]" placeholder={"Contenu — paragraphes séparés par une ligne vide. Sous-titres : ## Mon titre"} value={editing.content} onChange={set("content")} />
                <label className="flex items-center gap-2 text-sm text-slate-400">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="article-input-published" />
                  Publié
                </label>
                <button data-testid="article-save-btn" disabled={busy || !editing.title} onClick={save} className="btn-primary w-full disabled:opacity-50">
                  {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
